import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import axios from 'axios'

export interface PublicBrandingState {
  companyName: string
  primaryLogoUrl: string
  tagline: string
  backgroundBannerUrl: string
  address: string
  logoVersion: number
}

interface BrandContextValue {
  branding: PublicBrandingState
  refreshBranding: () => Promise<PublicBrandingState>
}

const DEFAULT_BRANDING: PublicBrandingState = {
  companyName: 'Maa Mundeshwari Diagnostic',
  primaryLogoUrl: '',
  tagline: 'Fast, Accurate & Trusted',
  backgroundBannerUrl: '',
  address: '',
  logoVersion: Date.now(),
}

const resolvePublicAssetUrl = (value: string): string => {
  const raw = (value || '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return raw

  const normalized = raw.replace(/^\/+/, '').replace(/^api\/files\//i, '').replace(/^uploads\//i, '')
  if (!normalized) return ''

  if (raw.startsWith('/api/files/') || raw.startsWith('/uploads/')) {
    return `${API_BASE}/api/files/${normalized}`
  }
  if (raw.startsWith('api/files/')) {
    return `${API_BASE}/api/files/${normalized}`
  }
  if (raw.startsWith('uploads/')) {
    return `${API_BASE}/api/files/${normalized}`
  }

  return `${API_BASE}/${normalized}`
}

const STORAGE_KEY = 'public-branding-cache'
const API_BASE =
  (process as any).env.REACT_APP_API_BASE_URL ||
  ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://127.0.0.1:8000'
    : window.location.origin)

const BrandContext = createContext<BrandContextValue | undefined>(undefined)

const normalizeBranding = (payload: any, previous?: PublicBrandingState): PublicBrandingState => {
  const companyNameCandidate = [
    payload?.company_name,
    payload?.name,
    payload?.company_profile?.company_name,
    payload?.companyName,
    previous?.companyName,
    DEFAULT_BRANDING.companyName,
  ].find((name) => typeof name === 'string' && name.trim() && name.trim().toLowerCase() !== 'diagnostic erp')
  const companyName = (companyNameCandidate || DEFAULT_BRANDING.companyName).trim()
  const rawLogoUrl = (
    payload?.primary_logo_url ||
    payload?.logo_url ||
    payload?.assets?.primary_logo_url ||
    payload?.branding_asset_manager?.primary_logo_web ||
    payload?.primaryLogoUrl ||
    previous?.primaryLogoUrl ||
    ''
  ).trim()
  const tagline = (
    payload?.tagline ||
    payload?.company_profile?.tagline ||
    previous?.tagline ||
    DEFAULT_BRANDING.tagline
  ).trim()
  const backgroundBannerUrl = (
    payload?.background_banner_url ||
    payload?.assets?.background_banner_url ||
    payload?.branding_asset_manager?.background_banner ||
    payload?.backgroundBannerUrl ||
    previous?.backgroundBannerUrl ||
    ''
  ).trim()
  const address = (
    payload?.address ||
    [payload?.city, payload?.state].filter(Boolean).join(', ') ||
    previous?.address ||
    ''
  ).trim()
  return {
    companyName,
    primaryLogoUrl: resolvePublicAssetUrl(rawLogoUrl),
    tagline,
    backgroundBannerUrl: resolvePublicAssetUrl(backgroundBannerUrl),
    address,
    logoVersion: Date.now(),
  }
}

const readStoredBranding = (): PublicBrandingState | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return normalizeBranding(parsed)
  } catch {
    return null
  }
}

const persistBranding = (branding: PublicBrandingState) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(branding))
  } catch {
    // best-effort cache only
  }
}

export function BrandProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<PublicBrandingState>(() => readStoredBranding() || DEFAULT_BRANDING)
  const brandingRef = useRef(branding)

  // Keep the latest branding payload available without making refreshBranding
  // depend on the state object itself.
  useEffect(() => {
    brandingRef.current = branding
  }, [branding, brandingRef])

  const refreshBranding = useCallback(async () => {
    const previous = brandingRef.current
    const endpoints = [
      `${API_BASE}/api/public/branding`,
      '/api/public/branding',
    ]

    for (const endpoint of endpoints) {
      try {
        const response = await axios.get(endpoint)
        const next = normalizeBranding(response.data, previous)
        brandingRef.current = next
        persistBranding(next)
        setBranding(next)
        return next
      } catch {
        // Try the next candidate endpoint.
      }
    }

    const fallback = normalizeBranding({}, previous)
    brandingRef.current = fallback
    setBranding(fallback)
    return fallback
  }, [])

  useEffect(() => {
    void refreshBranding()

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY || !event.newValue) return
      try {
        const parsed = JSON.parse(event.newValue)
        setBranding((previous) => normalizeBranding(parsed, previous))
      } catch {
        // ignore malformed storage writes
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshBranding()
      }
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener('focus', refreshBranding)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('focus', refreshBranding)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [refreshBranding])

  const value = useMemo<BrandContextValue>(() => ({
    branding,
    refreshBranding,
  }), [branding, refreshBranding])

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
}

export function useBrandContext() {
  const context = useContext(BrandContext)
  if (!context) {
    throw new Error('useBrandContext must be used within a BrandProvider')
  }
  return context
}
